import React, { useState, useEffect } from 'react';
import { creatorAPI } from '../../services/api';
import { Flag, Send, Loader, CheckCircle, Clock, XCircle, AlertCircle, ChevronDown } from 'lucide-react';

const CreatorReports = ({ channelId }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const [reportType, setReportType] = useState('misleading_timestamp');
  const [targetType, setTargetType] = useState('peak_moment');
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const data = await creatorAPI.getReports();
      setReports(data.reports || []);
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetId.trim() || !reason.trim()) return;
    setSubmitting(true);
    setMessage('');
    try {
      await creatorAPI.submitReport({ reportType, targetType, targetId: targetId.trim(), reason: reason.trim(), details: details.trim() });
      setMessage('Report submitted successfully');
      setTargetId(''); setReason(''); setDetails('');
      fetchReports();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const statusConfig = {
    pending:   { icon: Clock,        color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
    reviewing: { icon: AlertCircle,  color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
    resolved:  { icon: CheckCircle,  color: '#10B981', bg: 'rgba(16,185,129,0.08)' },
    dismissed: { icon: XCircle,      color: '#EF4444', bg: 'rgba(239,68,68,0.08)' },
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', boxSizing: 'border-box',
    background: '#0A0A0F', border: '1px solid #1A1A2E', borderRadius: '8px',
    color: '#E5E5E5', fontSize: '14px', outline: 'none', fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'none', cursor: 'pointer',
    backgroundImage: 'none', // we use custom icon overlay
  };

  return (
    <div>
      {/* Submit Form */}
      <div style={{
        background: '#111118', borderRadius: '14px', padding: '24px',
        border: '1px solid #1A1A2E', marginBottom: '24px',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          fontSize: '16px', fontWeight: '600', color: '#FFF', marginBottom: '20px',
        }}>
          <Flag size={18} style={{ color: '#8B5CF6' }} />
          Submit a Report
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Two selects side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '12px', color: '#555', fontWeight: '500', marginBottom: '6px', display: 'block' }}>Report Type</label>
              <div style={{ position: 'relative' }}>
                <select style={selectStyle} value={reportType} onChange={e => setReportType(e.target.value)}>
                  <option value="misleading_timestamp">Misleading Timestamp</option>
                  <option value="wrong_description">Wrong Description</option>
                  <option value="removal_request">Removal Request</option>
                  <option value="abuse">Abuse</option>
                  <option value="bug">Bug</option>
                </select>
                <ChevronDown size={14} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#555', pointerEvents: 'none' }} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#555', fontWeight: '500', marginBottom: '6px', display: 'block' }}>Target Type</label>
              <div style={{ position: 'relative' }}>
                <select style={selectStyle} value={targetType} onChange={e => setTargetType(e.target.value)}>
                  <option value="peak_moment">Peak Moment</option>
                  <option value="comment">Comment</option>
                  <option value="user">User</option>
                </select>
                <ChevronDown size={14} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#555', pointerEvents: 'none' }} />
              </div>
            </div>
          </div>

          <div>
            <label style={{ fontSize: '12px', color: '#555', fontWeight: '500', marginBottom: '6px', display: 'block' }}>Target ID</label>
            <input
              style={inputStyle}
              placeholder="ID of the peak moment, comment, or user"
              value={targetId}
              onChange={e => setTargetId(e.target.value)}
              onFocus={e => e.target.style.borderColor = '#8B5CF6'}
              onBlur={e => e.target.style.borderColor = '#1A1A2E'}
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', color: '#555', fontWeight: '500', marginBottom: '6px', display: 'block' }}>Reason</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
              placeholder="Describe the issue..."
              value={reason}
              onChange={e => setReason(e.target.value)}
              maxLength={500}
              onFocus={e => e.target.style.borderColor = '#8B5CF6'}
              onBlur={e => e.target.style.borderColor = '#1A1A2E'}
            />
          </div>

          <div>
            <label style={{ fontSize: '12px', color: '#555', fontWeight: '500', marginBottom: '6px', display: 'block' }}>Additional Details (optional)</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: '60px' }}
              placeholder="Any additional context..."
              value={details}
              onChange={e => setDetails(e.target.value)}
              maxLength={1000}
              onFocus={e => e.target.style.borderColor = '#8B5CF6'}
              onBlur={e => e.target.style.borderColor = '#1A1A2E'}
            />
          </div>

          {message && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 16px', borderRadius: '8px', fontSize: '13px',
              background: message.includes('Failed') ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)',
              color: message.includes('Failed') ? '#EF4444' : '#10B981',
              border: `1px solid ${message.includes('Failed') ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)'}`,
            }}>
              {message.includes('Failed') ? <AlertCircle size={14} /> : <CheckCircle size={14} />}
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !targetId.trim() || !reason.trim()}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '12px', border: 'none', borderRadius: '8px',
              cursor: (submitting || !targetId.trim() || !reason.trim()) ? 'not-allowed' : 'pointer',
              fontSize: '14px', fontWeight: '600',
              background: (submitting || !targetId.trim() || !reason.trim()) ? '#1A1A2E' : 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
              color: '#FFF', opacity: (submitting || !targetId.trim() || !reason.trim()) ? 0.5 : 1,
              transition: 'all 0.15s',
            }}
          >
            {submitting
              ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Submitting...</>
              : <><Send size={14} /> Submit Report</>}
          </button>
        </form>
      </div>

      {/* Report History */}
      <div style={{
        background: '#111118', borderRadius: '14px', padding: '24px',
        border: '1px solid #1A1A2E',
      }}>
        <div style={{
          fontSize: '16px', fontWeight: '600', color: '#FFF', marginBottom: '20px',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <Clock size={18} style={{ color: '#8B5CF6' }} />
          Report History
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
            <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : reports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#444', fontSize: '13px' }}>
            No reports submitted yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {reports.map(r => {
              const sc = statusConfig[r.status] || statusConfig.pending;
              const StatusIcon = sc.icon;
              return (
                <div key={r._id} style={{
                  padding: '14px 16px', borderRadius: '10px',
                  background: 'rgba(255,255,255,0.02)', border: '1px solid #1A1A2E',
                  transition: 'background 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#E5E5E5', textTransform: 'capitalize' }}>
                      {r.reportType?.replace(/_/g, ' ')}
                    </span>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                      background: sc.bg, color: sc.color,
                    }}>
                      <StatusIcon size={12} /> {r.status}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#999', marginBottom: '4px' }}>{r.reason}</div>
                  <div style={{ fontSize: '11px', color: '#555' }}>
                    {r.targetType} &middot; {new Date(r.createdAt).toLocaleDateString()}
                  </div>
                  {r.resolution && (
                    <div style={{
                      fontSize: '12px', color: '#10B981', marginTop: '8px',
                      padding: '8px 10px', borderRadius: '6px',
                      background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.1)',
                    }}>
                      Resolution: {r.resolution}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatorReports;
